/**
 * Centralized W3C trace context management.
 *
 * Provides a shared traceId per user action (page navigation or button press).
 * Each API request under the same action gets a new spanId but shares the traceId,
 * enabling end-to-end correlation between PostHog events and Better Stack logs.
 *
 * Usage:
 *   - Call `startNewTrace()` on page navigation or button press
 *   - `buildTraceparent()` is called automatically by fetch-helpers per request
 *   - Backend extracts traceId/spanId via `extractTraceContext(req)`
 */

/** Generate `len` random bytes as a hex string — works in both browser and Node.js */
function toHex(len: number): string {
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    // Browser / Edge runtime
    return Array.from(globalThis.crypto.getRandomValues(new Uint8Array(len)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }
  // Node.js server runtime
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require('crypto').randomBytes(len).toString('hex')
}

// Module-level trace state — shared across all fetch calls in the same browser context
let currentTraceId: string = toHex(16)

/**
 * Start a new trace (call on page navigation or user-initiated action).
 * All subsequent fetchWithErrorHandling calls will share this traceId.
 * @returns the new traceId
 */
export function startNewTrace(): string {
  currentTraceId = toHex(16)
  return currentTraceId
}

/** Returns the currently active traceId */
export function getCurrentTraceId(): string {
  return currentTraceId
}

/**
 * Build a W3C traceparent header for a single request.
 * Reuses the current traceId, generates a new spanId per request.
 */
export function buildTraceparent(): { traceparent: string; traceId: string; spanId: string } {
  const spanId = toHex(8)
  return {
    traceparent: `00-${currentTraceId}-${spanId}-01`,
    traceId: currentTraceId,
    spanId,
  }
}

/**
 * Extract trace context from an incoming server-side request.
 * Falls back to a generated traceId if no traceparent header is present (e.g. batch jobs).
 * Works with both Next.js Request (headers.get) and plain objects (headers[name]).
 */
export function extractTraceContext(req: any): {
  traceId: string
  spanId: string
  sessionId: string
} {
  const getHeader = (name: string): string => {
    if (typeof req.headers?.get === 'function') return req.headers.get(name) ?? ''
    if (req.headers) return req.headers[name] ?? ''
    return ''
  }

  const traceparent = getHeader('traceparent')
  const parts = traceparent.split('-')
  // W3C traceparent: 00-{traceId(32)}-{spanId(16)}-{flags}
  const traceId = parts[1] || toHex(16)
  const spanId = parts[2] || ''
  const sessionId = getHeader('x-posthog-session-id')

  return { traceId, spanId, sessionId }
}
